import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "hooks/useApi";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useNotification } from "./useNotification";

type Props = {
  entityId: string;
  seasonId: string;
  fixtureId: string;
};

export const useDeleteFixtureTask = () => {
  const { formatMessage } = useIntl();
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();

  return useMutation({
    mutationFn: ({ entityId, seasonId, fixtureId }: Props) => {
      return callApi(
        `/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/fixtures/${fixtureId}/task`,
        { method: "DELETE", body: undefined }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["fixtures", sport, organizationId],
      });
      showSuccessNotification(formatMessage({ id: "notification.task.deleted" }));
    },
  });
};

export const useSubmitFixtureTask = () => {
  const { formatMessage } = useIntl();
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();

  return useMutation({
    mutationFn: ({ entityId, seasonId, fixtureId }: Props) => {
      return callApi(
        `/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/fixtures/${fixtureId}/task/submit`,
        {
          method: "PATCH",
          body: {},
        }
      );
    },
    onSuccess: (_, { entityId, seasonId, fixtureId }: Props) => {
      queryClient.invalidateQueries({
        queryKey: ["fixture", sport, organizationId, entityId, seasonId, fixtureId],
      });
      showSuccessNotification(formatMessage({ id: "notification.task.submitted" }));
    },
  });
};

export const useRecallFixtureTask = () => {
  const { formatMessage } = useIntl();
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();

  return useMutation({
    mutationFn: ({ entityId, seasonId, fixtureId }: Props) => {
      return callApi(
        `/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/fixtures/${fixtureId}/task/recall`,
        {
          method: "PATCH",
          body: {},
        }
      );
    },
    onSuccess: (_, { entityId, seasonId, fixtureId }: Props) => {
      queryClient.invalidateQueries({
        queryKey: ["fixture", sport, organizationId, entityId, seasonId, fixtureId],
      });
      showSuccessNotification(formatMessage({ id: "notification.task.recalled" }));
    },
  });
};

export const useRejectFixtureTask = () => {
  const { formatMessage } = useIntl();
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();

  return useMutation({
    mutationFn: ({ entityId, seasonId, fixtureId, data }: Props & { data: any }) => {
      return callApi(
        `/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/fixtures/${fixtureId}/task/reject`,
        {
          method: "PATCH",
          body: data,
        }
      );
    },
    onSuccess: (_, { entityId, seasonId, fixtureId }: Props & { data: any }) => {
      queryClient.invalidateQueries({
        queryKey: ["fixture", sport, organizationId, entityId, seasonId, fixtureId],
      });
      showSuccessNotification(formatMessage({ id: "notification.task.rejected" }));
    },
  });
};

export const useReviewFixtureTask = () => {
  const { formatMessage } = useIntl();
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();

  return useMutation({
    mutationFn: ({ entityId, seasonId, fixtureId }: Props) => {
      return callApi(
        `/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/fixtures/${fixtureId}/task/review`,
        {
          method: "PATCH",
          body: {},
        }
      );
    },
    onSuccess: (_, { entityId, seasonId, fixtureId }: Props) => {
      queryClient.invalidateQueries({
        queryKey: ["fixture", sport, organizationId, entityId, seasonId, fixtureId],
      });
      showSuccessNotification(formatMessage({ id: "notification.task.reviewStarted" }));
    },
  });
};

export const useApproveFixtureTask = () => {
  const { formatMessage } = useIntl();
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();

  return useMutation({
    mutationFn: ({ entityId, seasonId, fixtureId }: Props) => {
      return callApi(
        `/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/fixtures/${fixtureId}/task/approve`,
        { method: "PATCH", body: {} }
      );
    },
    onSuccess: (_, { entityId, seasonId, fixtureId }: Props) => {
      queryClient.invalidateQueries({
        queryKey: ["fixture", sport, organizationId, entityId, seasonId, fixtureId],
      });
      showSuccessNotification(formatMessage({ id: "notification.task.approved" }));
    },
  });
};

export const usePublishFixtureTask = () => {
  const { formatMessage } = useIntl();
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();

  return useMutation({
    mutationFn: ({ entityId, seasonId, fixtureId }: Props) => {
      return callApi(
        `/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/fixtures/${fixtureId}/task/publish`,
        { method: "PATCH", body: {} }
      );
    },
    onSuccess: (_, { entityId, seasonId, fixtureId }: Props) => {
      queryClient.invalidateQueries({
        queryKey: ["fixture", sport, organizationId, entityId, seasonId, fixtureId],
      });
      showSuccessNotification(formatMessage({ id: "notification.task.publishingStarted" }));
    },
  });
};
